import React, { useState, useEffect } from 'react';
import { auth } from '../../config/firebase';
import axios from 'axios';
import '../../styles/AnniversaryTimeline.css';
import { Link } from "react-router-dom";
import { Hearts } from 'react-loader-spinner';

const AnniversaryTimeline = () => {
    const [relationshipData, setRelationshipData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [timelineData, setTimelineData] = useState({
        daysUntilAnniversary: 0,
        daysUntilMonthiversary: 0,
        totalDaysInRelationship: 0,
        percentageToAnniversary: 0,
        percentageToMonthiversary: 0
    });

    useEffect(() => {
        const fetchRelationshipData = async () => {
            const currentUser = auth.currentUser;

            if (!currentUser) {
                setLoading(false);
                return;
            }
            try {
                const response = await axios.get('https://api.frazza.online/relationship', {
                    params: { userId: currentUser.uid }
                });

                if (response.data.success) {
                    setRelationshipData(response.data.data);
                } else {
                    console.log("No relationship data found for current user");
                }
            } catch (error) {
                console.error("Error fetching relationship data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchRelationshipData();
    }, []);

    useEffect(() => {
        if (relationshipData && relationshipData.startDate) {
            calculateTimelineData(relationshipData.startDate);
        }
    }, [relationshipData]);

    const calculateTimelineData = (startDate) => {
        // Convert Firebase timestamp to JavaScript Date if needed
        const start = startDate.toDate ? startDate.toDate() : new Date(startDate);
        const today = new Date();

        // Calculate days in relationship so far
        const totalDaysInRelationship = Math.floor((today - start) / (1000 * 60 * 60 * 24));

        // Calculate next anniversary
        const nextAnniversary = new Date(start);
        nextAnniversary.setFullYear(today.getFullYear());
        if (nextAnniversary < today) {
            nextAnniversary.setFullYear(today.getFullYear() + 1);
        }

        const daysUntilAnniversary = Math.ceil((nextAnniversary - today) / (1000 * 60 * 60 * 24));

        // Calculate next monthiversary
        const getNextMonthiversary = () => {
            const startDay = start.getDate();
            let nextMonth = today.getMonth();
            let nextYear = today.getFullYear();

            let nextMonthiversary = new Date(nextYear, nextMonth, startDay);

            if (nextMonthiversary <= today) {
                nextMonth++;
                if (nextMonth > 11) {
                    nextMonth = 0;
                    nextYear++;
                }

                // Handle months with fewer days
                const lastDayOfMonth = new Date(nextYear, nextMonth + 1, 0).getDate();
                const targetDay = Math.min(startDay, lastDayOfMonth);
                nextMonthiversary = new Date(nextYear, nextMonth, targetDay);
            }

            return nextMonthiversary;
        };

        const nextMonthiversary = getNextMonthiversary();
        const daysUntilMonthiversary = Math.ceil((nextMonthiversary - today) / (1000 * 60 * 60 * 24));

        // Calculate percentages
        const daysInYear = 365;
        const daysUntilNextAnniversary = Math.ceil((nextAnniversary - today) / (1000 * 60 * 60 * 24));
        const daysSinceLastAnniversary = daysInYear - daysUntilNextAnniversary;

        // Calculate days in month cycle
        const lastMonthiversary = new Date(nextMonthiversary);
        lastMonthiversary.setMonth(lastMonthiversary.getMonth() - 1);
        const daysInMonthCycle = Math.ceil((nextMonthiversary - lastMonthiversary) / (1000 * 60 * 60 * 24));
        const daysSinceLastMonthiversary = Math.ceil((today - lastMonthiversary) / (1000 * 60 * 60 * 24));

        const percentageToAnniversary = (daysSinceLastAnniversary / daysInYear) * 100;
        const percentageToMonthiversary = (daysSinceLastMonthiversary / daysInMonthCycle) * 100;

        setTimelineData({
            daysUntilAnniversary,
            daysUntilMonthiversary,
            totalDaysInRelationship,
            percentageToAnniversary: Math.min(100, Math.max(0, percentageToAnniversary)),
            percentageToMonthiversary: Math.min(100, Math.max(0, percentageToMonthiversary))
        });
    };

    if (loading) return <div className="loading-container"><Hearts height="80" width="80" color="#f97316" ariaLabel="hearts-loading" visible={true} /><p>Loading timeline...</p></div>;
    if (!relationshipData || !relationshipData.startDate) {
        return (
            <div className="no-data-container">
                <p>Oh, it seems you don't have a partner connected yet.</p>
                <p>Connect with your partner and come back!</p>
                <Link to="/dashboard/profil" className="profile-link">
                    Go to Profile
                </Link>
            </div>
        );
    }

    return (
        <div className="anniversary-timeline">
            <h3>Your Relationship Timeline</h3>

            <div className="days-together">
                <span className="days-count">{timelineData.totalDaysInRelationship}</span>
                <span className="days-label">Days Together</span>
            </div>

            <div className="timeline-container">
                <div className="timeline-section">
                    <h4>Next Monthly Milestone</h4>
                    <div className="progress-bar">
                        <div className="progress-fill" style={{ width: `${timelineData.percentageToMonthiversary}%` }}></div>
                    </div>
                    <p>{timelineData.daysUntilMonthiversary} days to go</p>
                </div>

                <div className="timeline-section">
                    <h4>Next Anniversary</h4>
                    <div className="progress-bar">
                        <div className="progress-fill anniversary-fill" style={{ width: `${timelineData.percentageToAnniversary}%` }}></div>
                    </div>
                    <p>{timelineData.daysUntilAnniversary} days to go</p>
                </div>
            </div>
        </div>
    );
};

export default AnniversaryTimeline;
